import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import BrandLogo from '~/assets/images/logo/brands'
import configs from '~/env.json'

import { CAMPAIGN_LOCK_REASONS, CAMPAIGN_STATUS } from '~/helpers/constants'
import { isWebView } from '~/helpers/webView'
import useTracking from '~/services/tracking'
import { Container, Flex, FloraButton, FloraTypography } from '@grupoboticario/flora-react'
import { ArrowLeftIcon } from '@grupoboticario/flora-react-icons'

const brandComponentsSettings = {
  BOT: { alt: 'Logo O Boticário', width: '146', height: '20' },
  QDB: { alt: 'Logo Quem Disse, Berenice?', width: '113', height: '32' },
  VULT: { alt: 'Logo Vult', width: '170', height: '48' },
  AUS: { alt: 'Logo Australian Gold', width: '170', height: '110' }
}

const CAMPAIGN_ENDED_TITLE = 'Esta campanha chegou ao fim'
const CAMPAIGN_TEMPORARILY_CLOSED_TITLE = 'Campanha indisponível no momento'

const getCampaignEndedDescription = (campaignDisplayName) =>
  `O período de participação da campanha ${campaignDisplayName} terminou. Continue aproveitando nossas novidades e ofertas na loja oficial.`

const getCampaignTemporarilyClosedDescription = (campaignDisplayName) =>
  `O acesso à campanha ${campaignDisplayName} ainda não está liberado. Por favor, tente novamente mais tarde.`

export default function ErrorCampaign() {
  const tracking = useTracking()
  const { campaignLockReasons, campaignName, consumerMenu, campaignStatus } = useSelector(
    (state) => state?.campaignReducers?.campaign
  )

  const isCampaignEnded =
    campaignStatus === CAMPAIGN_STATUS.DISABLED ||
    campaignLockReasons?.includes(CAMPAIGN_LOCK_REASONS.BOOKING_PERIOD_ALREADY_FINISHED)

  /**
   * Campanhas de MKT Regional podem ter background personalizados. Para manter um padrão de tela de erro consistente,
   * esta configuração é necessária para forçar o background branco independente do BG herdado da campanha.
   */
  useEffect(() => {
    document.body.style.backgroundColor = '#FFFFFF'
    return () => {
      document.body.style.backgroundColor = ''
    }
  }, [])

  useEffect(() => {
    tracking({
      event: isCampaignEnded ? 'campanha_encerrada_page_view' : 'campanha_indisponivel_page_view'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCampaignEnded])

  const branch = configs?.REACT_APP_BRANCH || 'BOT'
  const brandSettings = brandComponentsSettings[branch] || brandComponentsSettings.BOT
  const consumerMenuRules = consumerMenu?.find((rule) => rule?.item === 'REGULAMENTO')
  const campaignDisplayName = campaignName || ''

  return (
    <Flex
      direction='column'
      css={{
        width: '100%',
        minHeight: '100vh',
        paddingTop: '$20',
        paddingBottom: '$6',
        gap: '$16'
      }}
      role='main'
    >
      <Flex css={{ width: '100%' }}>
        <Container centerContent size='small' css={{ gap: '$6', textAlign: 'left', width: '100%' }}>
          <Flex justify='center' css={{ width: '100%' }}>
            <BrandLogo width={brandSettings.width} height={brandSettings.height} aria-label={brandSettings.alt} />
          </Flex>

          <FloraTypography
            as='h1'
            fontSize='title'
            fontWeight='regular'
            id='error-campaign-title'
            css={{
              margin: 0,
              color: '$nonInteractivePredominant',
              textAlign: 'left'
            }}
          >
            {isCampaignEnded ? CAMPAIGN_ENDED_TITLE : CAMPAIGN_TEMPORARILY_CLOSED_TITLE}
          </FloraTypography>

          <FloraTypography
            as='p'
            fontSize='bodyLargeStandard'
            fontWeight='regular'
            css={{ textAlign: 'left', color: '$nonInteractivePredominant' }}
          >
            {isCampaignEnded
              ? getCampaignEndedDescription(campaignDisplayName)
              : getCampaignTemporarilyClosedDescription(campaignDisplayName)}
          </FloraTypography>
        </Container>
      </Flex>

      <Flex css={{ width: '100%', marginTop: 'auto', '@tablet': { marginTop: 0 } }}>
        <Container centerContent size='small' css={{ gap: '$2', width: '100%' }}>
          {isCampaignEnded && consumerMenuRules?.link && (
            <FloraButton
              hierarchy='primary'
              size='large'
              surfaceColor='light'
              isFull
              as='a'
              href={consumerMenuRules?.link}
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Ler regulamento da campanha'
              onClick={() => tracking({ event: 'campanha_encerrada_regulamento_click' })}
            >
              Ler regulamento
            </FloraButton>
          )}

          {isWebView() && (
            <FloraButton
              hierarchy='secondary'
              size='large'
              surfaceColor='light'
              isFull
              has='iconLeft'
              icon={<ArrowLeftIcon />}
              onClick={() => {
                tracking({
                  event: isCampaignEnded ? 'campanha_encerrada_voltar_click' : 'campanha_indisponivel_voltar_click'
                })
                window.Native?.close?.()
              }}
              aria-label='Voltar para a página anterior'
            >
              Voltar
            </FloraButton>
          )}
        </Container>
      </Flex>
    </Flex>
  )
}
